/**
 * Case Detail Page - timeline, notes, email, attachments
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Menu,
  LinearProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  AttachFile as AttachIcon,
  Person as PersonIcon,
  Note as NoteIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Case, CaseActivity, CASE_EMAIL_PLACEHOLDERS, BODY_PLACEHOLDER } from '../types/case';
import { AccountAsset } from '../types/account';
import { AccountContact } from '../types/account';
import apiService from '../services/api';
import CaseFormDialog from '../components/CaseFormDialog';
import RelatedTasksBlock from '../components/RelatedTasksBlock';
import { stripHtml, isQuillNoteEmpty, NOTE_QUILL_MODULES } from '../utils/noteQuill';
import { isMspAgreementServiceType } from '../utils/mspAgreement';
import { looksLikeHtml } from '../utils/htmlContent';

/** Reduce <p> margins in HTML for email – prevents large gaps between lines */
const compactEmailHtml = (html: string): string =>
  html.replace(/<p>/gi, '<p style="margin:0 0 0.35em 0">');

type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error';

const CaseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [accountContacts, setAccountContacts] = useState<AccountContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [templates, setTemplates] = useState<{ id: string; name: string; subject?: string; body: string }[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<{ id: string; filename: string; content_type?: string | null } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTextContent, setPreviewTextContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeResults, setMergeResults] = useState<Case[]>([]);
  const [activityMenuAnchor, setActivityMenuAnchor] = useState<null | HTMLElement>(null);
  const [activityMenuActivity, setActivityMenuActivity] = useState<CaseActivity | null>(null);
  const [mergeSelectedId, setMergeSelectedId] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);
  const [caseWorkflow, setCaseWorkflow] = useState<{
    id: string;
    template_name: string;
    started_at?: string;
    completed_at?: string;
    progress_percent: number;
    steps: { id: string; step_name: string; step_description?: string; sort_order: number; status: string }[];
  } | null>(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [applyWorkflowOpen, setApplyWorkflowOpen] = useState(false);
  const [workflowTemplates, setWorkflowTemplates] = useState<{ id: string; name: string }[]>([]);
  const [applyWorkflowTemplateId, setApplyWorkflowTemplateId] = useState('');
  const [applyWorkflowSubmitting, setApplyWorkflowSubmitting] = useState(false);
  const [emailViewOpen, setEmailViewOpen] = useState(false);
  const [emailViewData, setEmailViewData] = useState<{
    to: string;
    subject: string;
    cc?: string;
    bcc?: string;
    body?: string;
    body_html?: string;
    isReply?: boolean;
  } | null>(null);
  const [fieldOptions, setFieldOptions] = useState<{
    status: { value: string; label: string; color?: string }[];
    priority: { value: string; label: string; color?: string }[];
  }>({ status: [], priority: [] });
  /** Inline status/priority save from summary card */
  const [inlineSaving, setInlineSaving] = useState<'status' | 'priority' | null>(null);
  const [accountAssets, setAccountAssets] = useState<AccountAsset[]>([]);
  const [accountAssetsLoading, setAccountAssetsLoading] = useState(false);
  const [linkedAssetSaving, setLinkedAssetSaving] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  /** For summary card: detect MSP linked service + inline MSP hours */
  const [caseSummaryServices, setCaseSummaryServices] = useState<
    { id: string; service_id: string; service_type: string; service_type_name?: string | null }[]
  >([]);
  const [caseOverviewMspUse, setCaseOverviewMspUse] = useState(false);
  const [caseOverviewMspHours, setCaseOverviewMspHours] = useState('');
  const [caseOverviewMspSaving, setCaseOverviewMspSaving] = useState(false);
  const [caseOverviewMspBalanceHint, setCaseOverviewMspBalanceHint] = useState<string | null>(null);
  const [upstreamTicketRef, setUpstreamTicketRef] = useState('');
  const [upstreamSaving, setUpstreamSaving] = useState(false);

  const FALLBACK_STATUS_OPTIONS: { value: string; label: string }[] = [
    { value: 'new', label: 'New' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'end_user_reply', label: 'End User Reply' },
    { value: 'awaiting_end_user', label: 'Awaiting End User' },
    { value: 'awaiting_upstream_carrier', label: 'Awaiting Upstream Carrier' },
    { value: 'awaiting_nbn_appointment', label: 'Awaiting NBN Appointment' },
    { value: 'awaiting_supa_appointment', label: 'Awaiting Supa Appointment' },
    { value: 'escalated', label: 'Escalated' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'billing_system_update_required', label: 'Billing System Update Required' },
    { value: 'completed', label: 'Completed' },
  ];
  const FALLBACK_PRIORITY_OPTIONS: { value: string; label: string }[] = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
  ];

  const toChipColor = (c?: string | null): ChipColor =>
    (c && ['default', 'primary', 'success', 'warning', 'error'].includes(c) ? c : 'default') as ChipColor;
  const statusLabelMap: Record<string, string> = fieldOptions.status.length > 0
    ? Object.fromEntries(fieldOptions.status.map((o) => [o.value, o.label]))
    : {};
  const statusColorMap: Record<string, ChipColor> = fieldOptions.status.length > 0
    ? Object.fromEntries(fieldOptions.status.map((o) => [o.value, toChipColor(o.color)]))
    : {};
  const priorityLabelMap: Record<string, string> = fieldOptions.priority.length > 0
    ? Object.fromEntries(fieldOptions.priority.map((o) => [o.value, o.label]))
    : {};
  const priorityColorMap: Record<string, ChipColor> = fieldOptions.priority.length > 0
    ? Object.fromEntries(fieldOptions.priority.map((o) => [o.value, toChipColor(o.color)]))
    : {};

  const fetchCase = async (opts?: { quiet?: boolean }) => {
    if (!id) return;
    try {
      if (!opts?.quiet) setLoading(true);
      setError(null);
      const c = await apiService.getCase(id);
      setCaseData(c);
      setUpstreamTicketRef(c.upstream_ticket_ref || '');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load case');
    } finally {
      if (!opts?.quiet) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCase();
  }, [id]);

  const fetchWorkflow = React.useCallback(async () => {
    if (!id) return;
    setWorkflowLoading(true);
    try {
      const w = await apiService.getCaseWorkflow(id);
      setCaseWorkflow(w);
    } catch {
      setCaseWorkflow(null);
    } finally {
      setWorkflowLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchWorkflow();
  }, [id, fetchWorkflow]);

  useEffect(() => {
    apiService.getCaseTemplates().then((r) => setTemplates(r.templates || [])).catch(() => {});
  }, []);

  useEffect(() => {
    apiService.getCaseFieldOptionsGrouped().then((r) => setFieldOptions({ status: r.status || [], priority: r.priority || [] })).catch(() => {});
  }, []);

  const statusSelectOptions =
    fieldOptions.status.length > 0 ? fieldOptions.status : FALLBACK_STATUS_OPTIONS;
  const prioritySelectOptions =
    fieldOptions.priority.length > 0 ? fieldOptions.priority : FALLBACK_PRIORITY_OPTIONS;

  const handleInlineStatusChange = async (next: string) => {
    if (!id || !caseData || caseData.merged_into_id || next === caseData.status) return;
    setInlineSaving('status');
    setError(null);
    try {
      await apiService.updateCase(id, { status: next });
      await fetchCase({ quiet: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update status');
    } finally {
      setInlineSaving(null);
    }
  };

  const handleInlinePriorityChange = async (next: string) => {
    if (!id || !caseData || caseData.merged_into_id || next === caseData.priority) return;
    setInlineSaving('priority');
    setError(null);
    try {
      await apiService.updateCase(id, { priority: next });
      await fetchCase({ quiet: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update priority');
    } finally {
      setInlineSaving(null);
    }
  };

  useEffect(() => {
    if (!caseData) return;
    setCaseOverviewMspUse(!!caseData.msp_use_hours);
    setCaseOverviewMspHours(caseData.msp_hours_reserved || '');
  }, [caseData?.id, caseData?.msp_use_hours, caseData?.msp_hours_reserved]);

  useEffect(() => {
    if (!caseData?.account_id) {
      setCaseSummaryServices([]);
      return;
    }
    let cancelled = false;
    apiService
      .getAccountServices(caseData.account_id, { per_page: 500 })
      .then((r: any) => {
        if (!cancelled) setCaseSummaryServices(r.services || r || []);
      })
      .catch(() => {
        if (!cancelled) setCaseSummaryServices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [caseData?.account_id]);

  useEffect(() => {
    if (!caseData?.account_service_id) {
      setCaseOverviewMspBalanceHint(null);
      return;
    }
    const s = caseSummaryServices.find((x) => x.id === caseData.account_service_id);
    if (!s || !isMspAgreementServiceType(s.service_type, s.service_type_name)) {
      setCaseOverviewMspBalanceHint(null);
      return;
    }
    let cancelled = false;
    apiService
      .getMspBalance(caseData.account_service_id)
      .then((b: any) => {
        if (!cancelled) {
          setCaseOverviewMspBalanceHint(
            `Available: ${b.available_total} h (granted ${b.total_grants}, reserved ${b.reserved_total})`
          );
        }
      })
      .catch(() => {
        if (!cancelled) setCaseOverviewMspBalanceHint(null);
      });
    return () => {
      cancelled = true;
    };
  }, [caseData?.account_service_id, caseSummaryServices]);

  useEffect(() => {
    if (!caseData?.account_id) {
      setAccountAssets([]);
      return;
    }
    let cancelled = false;
    setAccountAssetsLoading(true);
    apiService
      .getAccountAssets(caseData.account_id)
      .then((r) => {
        if (!cancelled) setAccountAssets(r.assets || []);
      })
      .catch(() => {
        if (!cancelled) setAccountAssets([]);
      })
      .finally(() => {
        if (!cancelled) setAccountAssetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseData?.account_id]);

  const handleLinkedAssetChange = async (assetId: string) => {
    if (!id || !caseData || caseData.merged_into_id) return;
    setLinkedAssetSaving(true);
    setError(null);
    try {
      await apiService.updateCase(id, { account_asset_id: assetId ? assetId : null });
      await fetchCase({ quiet: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update linked asset');
    } finally {
      setLinkedAssetSaving(false);
    }
  };

  const openRelatedServiceInNewTab = () => {
    if (!caseData?.account_id || !caseData.account_service_id) return;
    const q = new URLSearchParams({ tab: 'services', service: caseData.account_service_id });
    window.open(`${window.location.origin}/accounts/${caseData.account_id}?${q.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const openAccountInNewTab = () => {
    if (!caseData?.account_id) return;
    window.open(`${window.location.origin}/accounts/${caseData.account_id}`, '_blank', 'noopener,noreferrer');
  };

  const handleCaseOverviewMspSave = async () => {
    if (!id || !caseData || caseData.merged_into_id) return;
    if (caseOverviewMspUse) {
      const h = parseFloat(caseOverviewMspHours || '0');
      if (!caseData.account_service_id || Number.isNaN(h) || h <= 0) {
        setError('Select a related MSP agreement service and enter hours greater than zero');
        return;
      }
    }
    const linked = caseSummaryServices.find((s) => s.id === caseData.account_service_id);
    if (caseOverviewMspUse && linked && !isMspAgreementServiceType(linked.service_type, linked.service_type_name)) {
      setError('Related service must be an MSP agreement to reserve hours');
      return;
    }
    setCaseOverviewMspSaving(true);
    setError(null);
    try {
      await apiService.updateCase(id, {
        msp_use_hours: caseOverviewMspUse,
        msp_hours: caseOverviewMspUse ? parseFloat(caseOverviewMspHours || '0') : undefined,
      });
      await fetchCase({ quiet: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update MSP hours');
    } finally {
      setCaseOverviewMspSaving(false);
    }
  };

  const handleUpstreamTicketRefSave = async () => {
    if (!id) return;
    setUpstreamSaving(true);
    try {
      await apiService.updateCase(id, { upstream_ticket_ref: upstreamTicketRef.trim() || null });
      await fetchCase({ quiet: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update upstream ticket reference');
    } finally {
      setUpstreamSaving(false);
    }
  };

  const openLinkedAssetInNewTab = () => {
    if (!caseData?.account_id || !caseData.account_asset_id) return;
    const q = new URLSearchParams({ tab: 'assets', asset: caseData.account_asset_id });
    window.open(`${window.location.origin}/accounts/${caseData.account_id}?${q.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const handleAddNote = async () => {
    const trimmed = noteText.trim();
    if (!id || !trimmed || isQuillNoteEmpty(trimmed)) return;
    setAddingNote(true);
    try {
      await apiService.addCaseNote(id, trimmed);
      setNoteText('');
      fetchCase();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleActivityMenuOpen = (event: React.MouseEvent<HTMLElement>, act: CaseActivity) => {
    setActivityMenuAnchor(event.currentTarget);
    setActivityMenuActivity(act);
  };

  const handleActivityMenuClose = () => {
    setActivityMenuAnchor(null);
    setActivityMenuActivity(null);
  };

  const handleDeleteNote = async () => {
    if (!id || !activityMenuActivity) return;
    const activityId = activityMenuActivity.id;
    handleActivityMenuClose();
    try {
      await apiService.deleteCaseActivity(id, activityId);
      fetchCase();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete note');
    }
  };

  const handleOpenApplyWorkflow = () => {
    setApplyWorkflowTemplateId('');
    apiService.listCaseWorkflowTemplates(true).then((r) => setWorkflowTemplates(r || [])).catch(() => setWorkflowTemplates([]));
    setApplyWorkflowOpen(true);
  };

  const handleApplyWorkflow = async () => {
    if (!id || !applyWorkflowTemplateId) return;
    setApplyWorkflowSubmitting(true);
    try {
      const w = await apiService.applyCaseWorkflow(id, applyWorkflowTemplateId);
      setCaseWorkflow(w);
      setApplyWorkflowOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to apply workflow');
    } finally {
      setApplyWorkflowSubmitting(false);
    }
  };

  const handleWorkflowStepStatusChange = async (stepId: string, status: string) => {
    if (!id || caseWorkflow?.completed_at) return;
    try {
      await apiService.updateCaseWorkflowStep(id, stepId, { status });
      fetchWorkflow();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update step');
    }
  };

  const handleCompleteWorkflow = async () => {
    if (!id) return;
    try {
      const w = await apiService.completeCaseWorkflow(id);
      setCaseWorkflow(w);
      await fetchCase({ quiet: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to complete workflow');
    }
  };

  const handleSendEmail = async () => {
    if (!id || !emailTo.trim() || !emailSubject.trim()) return;
    const userMessage = emailBody.trim();
    if (!userMessage) return;
    setSendingEmail(true);
    try {
      const selectedTemplate = selectedTemplateId ? templates.find((x) => x.id === selectedTemplateId) : null;
      let bodyToSend: string;
      let bodyHtmlToSend: string | undefined;
      if (selectedTemplate?.body?.includes('%%email_body%%')) {
        const userPlain = /<[^>]+>/.test(userMessage) ? stripHtml(userMessage) : userMessage;
        const userHtml = /<[^>]+>/.test(userMessage) ? userMessage : undefined;
        const combinedBody = selectedTemplate.body.replace(/%%email_body%%/g, userHtml ?? `<p>${userPlain.replace(/\n/g, '</p><p>')}</p>`);
        bodyToSend = stripHtml(combinedBody);
        bodyHtmlToSend = compactEmailHtml(combinedBody);
      } else {
        bodyToSend = /<[^>]+>/.test(userMessage) ? stripHtml(userMessage) : userMessage;
        bodyHtmlToSend = /<[^>]+>/.test(userMessage) ? compactEmailHtml(userMessage) : undefined;
      }
      await apiService.sendCaseEmail(id, {
        to_email: emailTo.trim(),
        cc_email: emailCc.trim() || undefined,
        bcc_email: emailBcc.trim() || undefined,
        subject: emailSubject,
        body: bodyToSend,
        body_html: bodyHtmlToSend,
        template_id: selectedTemplateId || undefined,
      });
      setEmailOpen(false);
      setEmailTo('');
      setEmailCc('');
      setEmailBcc('');
      setEmailSubject('');
      setEmailBody('');
      setSelectedTemplateId('');
      fetchCase();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const t = templates.find((x) => x.id === templateId);
    if (t) {
      setEmailSubject(t.subject || '');
      setEmailBody(''); // Body stays empty – user enters their message; template's %%email_body%% will be replaced on send
    } else {
      setEmailBody('');
    }
  };

  const fetchContactsForEmail = async () => {
    if (!caseData?.account_id) return;
    setContactsLoading(true);
    try {
      const { contacts } = await apiService.getAccountContacts(caseData.account_id);
      setAccountContacts(contacts || []);
    } catch {
      setAccountContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!id || !file) return;
    setUploading(true);
    try {
      await apiService.uploadCaseAttachment(id, file);
      fetchCase();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePreviewAttachment = (a: { id: string; filename: string; content_type?: string | null }) => {
    setPreviewAttachment({ id: a.id, filename: a.filename, content_type: a.content_type });
    setPreviewUrl(null);
    setPreviewTextContent(null);
    setPreviewOpen(true);
    setPreviewLoading(true);
    const apiUrl = `${process.env.REACT_APP_API_URL || window.location.origin}/api/cases/${id}/attachments/${a.id}/download`;
    const token = localStorage.getItem('access_token');
    fetch(apiUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const ct = (a.content_type || '').toLowerCase();
        const ext = a.filename.split('.').pop()?.toLowerCase() || '';
        const isText = ct.startsWith('text/') || ct.includes('json') || ct.includes('xml') || ['txt', 'json', 'xml', 'csv', 'log', 'md', 'yaml', 'yml'].includes(ext);
        if (isText) {
          blob.text().then((text) => {
            setPreviewTextContent(text);
            setPreviewLoading(false);
          });
        } else {
          setPreviewUrl(URL.createObjectURL(blob));
          setPreviewLoading(false);
        }
      })
      .catch(() => {
        setError('Failed to load preview');
        setPreviewLoading(false);
      });
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewTextContent(null);
    setPreviewAttachment(null);
  };

  const handleDownloadAttachment = (a: { id: string; filename: string }) => {
    const url = `${process.env.REACT_APP_API_URL || window.location.origin}/api/cases/${id}/attachments/${a.id}/download`;
    const token = localStorage.getItem('access_token');
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const lnk = document.createElement('a');
        lnk.href = u;
        lnk.download = a.filename;
        lnk.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => setError('Failed to download'));
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!id) return;
    try {
      await apiService.deleteCaseAttachment(id, attachmentId);
      fetchCase();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  const handleReopen = async () => {
    if (!id) return;
    setReopening(true);
    try {
      await apiService.reopenCase(id);
      setReopenOpen(false);
      fetchCase();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to reopen case');
    } finally {
      setReopening(false);
    }
  };

  const handleMergeSearch = async () => {
    if (!mergeSearch.trim()) {
      setMergeResults([]);
      return;
    }
    try {
      const res = await apiService.getCases({ q: mergeSearch, per_page: 20 });
      const filtered = (res.cases || []).filter((c: Case) => c.id !== id && !c.merged_into_id);
      setMergeResults(filtered);
      setMergeSelectedId(null);
    } catch {
      setMergeResults([]);
    }
  };

  const handleMergeConfirm = async () => {
    if (!id || !mergeSelectedId) return;
    setMerging(true);
    try {
      await apiService.mergeCase(id, mergeSelectedId);
      setMergeOpen(false);
      setMergeSearch('');
      setMergeResults([]);
      setMergeSelectedId(null);
      fetchCase();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to merge case');
    } finally {
      setMerging(false);
    }
  };

  const formatDateTime = (s: string | null | undefined) => {
    if (!s) return '-';
    return new Date(s).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'note': return <NoteIcon />;
      case 'email_sent': return <EmailIcon />;
      case 'customer_reply': return <EmailIcon />;
      case 'status_change': return <EditIcon />;
      case 'assignee_change': return <PersonIcon />;
      case 'reopened': return <EditIcon />;
      case 'merged': return <EditIcon />;
      default: return <NoteIcon />;
    }
  };

  if (!id) return null;
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!caseData) {
    return (
      <Alert severity="error" onClose={() => navigate(-1)}>
        Case not found
      </Alert>
    );
  }

  const richHtmlSx = {
    mb: 0,
    maxWidth: '100%',
    minWidth: 0,
    fontSize: '0.875rem',
    '& p': { margin: '0 0 0.35em 0' },
    '& p:last-child': { marginBottom: 0 },
    '& ul, & ol': { margin: '0.35em 0', paddingLeft: 1.5 },
    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, display: 'block' },
    '& table': { maxWidth: '100%', display: 'block', overflow: 'auto' },
    '& a': { wordBreak: 'break-all' as const },
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <BackIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: 1,
            flex: '1 1 280px',
            minWidth: 0,
            columnGap: 1,
            rowGap: 0.25,
          }}
        >
          <Typography component="span" variant="body2" fontWeight={600} color="text.secondary">
            {caseData.case_number}
          </Typography>
          <Typography component="span" variant="h6" fontWeight={600} sx={{ flex: '1 1 200px', minWidth: 0, lineHeight: 1.35 }}>
            {caseData.subject || '—'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {!caseData.merged_into_id && (
            <>
              <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                Edit
              </Button>
              {caseData.status === 'completed' && (
                <Button variant="outlined" size="small" color="primary" onClick={() => setReopenOpen(true)}>
                  Reopen
                </Button>
              )}
              <Button variant="outlined" size="small" onClick={() => setMergeOpen(true)}>
                Merge
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {caseData.merged_into_case_number && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This case was merged into <strong>{caseData.merged_into_case_number}</strong>.{' '}
          <Button size="small" onClick={() => navigate(`/crm/cases/${caseData.merged_into_id}`)}>
            Go to {caseData.merged_into_case_number}
          </Button>
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1.5,
            }}
          >
            <Grid container spacing={1.5} alignItems="flex-start">
              <Grid item xs={12}>
                <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.5 }}>
                  Linked customer account
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body1" fontWeight={600} sx={{ flex: '1 1 120px', minWidth: 0 }}>
                    {caseData.account_display_name || caseData.account_id}
                  </Typography>
                  {caseData.account_id && (
                    <IconButton
                      size="small"
                      onClick={openAccountInNewTab}
                      title="Open account in new tab"
                      aria-label="Open account in new tab"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.25 }}>
                  Due by
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.875rem' }}>
                    {caseData.due_by ? formatDateTime(caseData.due_by) : 'Not set'}
                  </Typography>
                  {caseData.due_by && caseData.status !== 'completed' && new Date(caseData.due_by) < new Date() && (
                    <Chip label="Overdue" size="small" color="error" variant="outlined" />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.25 }}>
                  Assignee
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                  {caseData.assignee_name || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.25 }}>
                  Type & origin
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                  {caseData.case_type === 'service'
                    ? 'Service'
                    : caseData.case_type === 'billing'
                      ? 'Billing'
                      : 'Other'}
                  {caseData.case_origin ? ` · ${caseData.case_origin}` : ''}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small" disabled={!!caseData.merged_into_id}>
                  <InputLabel id="case-detail-status-label">Status</InputLabel>
                  <Select
                    labelId="case-detail-status-label"
                    label="Status"
                    value={caseData.status}
                    onChange={(e) => handleInlineStatusChange(e.target.value)}
                    disabled={!!caseData.merged_into_id || inlineSaving === 'status'}
                    renderValue={(v) => {
                      const lbl = statusLabelMap[v] || statusSelectOptions.find((o) => o.value === v)?.label || v;
                      return <Chip label={lbl} size="small" color={statusColorMap[v] ?? 'default'} sx={{ height: 22 }} />;
                    }}
                  >
                    {statusSelectOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small" disabled={!!caseData.merged_into_id}>
                  <InputLabel id="case-detail-priority-label">Priority</InputLabel>
                  <Select
                    labelId="case-detail-priority-label"
                    label="Priority"
                    value={caseData.priority}
                    onChange={(e) => handleInlinePriorityChange(e.target.value)}
                    disabled={!!caseData.merged_into_id || inlineSaving === 'priority'}
                    renderValue={(v) => {
                      const lbl = priorityLabelMap[v] || prioritySelectOptions.find((o) => o.value === v)?.label || v;
                      return (
                        <Chip label={lbl} size="small" color={priorityColorMap[v] ?? 'default'} sx={{ height: 22 }} />
                      );
                    }}
                  >
                    {prioritySelectOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {(caseData.account_service_display || caseData.account_service_id) && (
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.25 }}>
                    Linked service
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ flex: '1 1 120px', minWidth: 0 }}>
                      {caseData.account_service_display ||
                        (() => {
                          const s = caseSummaryServices.find((x) => x.id === caseData.account_service_id);
                          return s ? `${s.service_id} (${s.service_type})` : caseData.account_service_id;
                        })()}
                    </Typography>
                    {caseData.account_service_id && (
                      <IconButton
                        size="small"
                        onClick={openRelatedServiceInNewTab}
                        title="Open service details in new tab"
                        aria-label="Open service details in new tab"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {(() => {
                    const linked = caseSummaryServices.find((s) => s.id === caseData.account_service_id);
                    const isMsp = linked && isMspAgreementServiceType(linked.service_type, linked.service_type_name);
                    if (!isMsp || caseData.merged_into_id) return null;
                    return (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                        }}
                      >
                        {caseOverviewMspBalanceHint && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {caseOverviewMspBalanceHint}
                          </Typography>
                        )}
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={caseOverviewMspUse}
                              onChange={(e) => setCaseOverviewMspUse(e.target.checked)}
                              disabled={caseOverviewMspSaving}
                            />
                          }
                          label="Use MSP agreement hours"
                        />
                        {caseOverviewMspUse && (
                          <TextField
                            label="Hours reserved"
                            type="number"
                            size="small"
                            fullWidth
                            inputProps={{ step: 0.25, min: 0 }}
                            value={caseOverviewMspHours}
                            onChange={(e) => setCaseOverviewMspHours(e.target.value)}
                            sx={{ mt: 1, maxWidth: 280 }}
                            disabled={caseOverviewMspSaving}
                          />
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1, display: 'block' }}
                          onClick={handleCaseOverviewMspSave}
                          disabled={caseOverviewMspSaving}
                        >
                          {caseOverviewMspSaving ? 'Saving…' : 'Save MSP allocation'}
                        </Button>
                      </Box>
                    );
                  })()}
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.25 }}>
                  Linked asset
                </Typography>
                {accountAssetsLoading ? (
                  <CircularProgress size={22} sx={{ my: 0.5 }} />
                ) : accountAssets.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No assets for this account
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', maxWidth: '100%' }}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ flex: '1 1 200px', minWidth: 0 }}
                      disabled={!!caseData.merged_into_id || linkedAssetSaving}
                    >
                      <InputLabel id="case-linked-asset-label">Asset</InputLabel>
                      <Select
                        labelId="case-linked-asset-label"
                        label="Asset"
                        displayEmpty
                        value={caseData.account_asset_id ?? ''}
                        onChange={(e) => handleLinkedAssetChange(e.target.value as string)}
                        renderValue={(v) => {
                          if (!v) return <em>None</em>;
                          return caseData.account_asset_display ?? accountAssets.find((a) => a.id === v)?.asset_name ?? v;
                        }}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {accountAssets.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.asset_name}
                            {a.model_number ? ` (${a.model_number})` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {caseData.account_asset_id && (
                      <IconButton
                        size="small"
                        onClick={openLinkedAssetInNewTab}
                        title="Open asset details in new tab"
                        aria-label="Open asset details in new tab"
                        disabled={!caseData.account_asset_id}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.25 }}>
                  Upstream ticket ref
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="e.g. Ticket #70607, VOC-12345"
                    value={upstreamTicketRef}
                    onChange={(e) => setUpstreamTicketRef(e.target.value)}
                    disabled={upstreamSaving || !!caseData.merged_into_id}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleUpstreamTicketRefSave(); }}
                    inputProps={{ maxLength: 500 }}
                  />
                  <Button size="small" variant="outlined" onClick={handleUpstreamTicketRefSave} disabled={upstreamSaving || !!caseData.merged_into_id}>
                    {upstreamSaving ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Workflow
            </Typography>
            {workflowLoading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : !caseWorkflow ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Apply a workflow template to track steps (open, in progress, complete) and see progress.
                </Typography>
                <Button fullWidth variant="outlined" size="small" onClick={handleOpenApplyWorkflow}>
                  Apply workflow
                </Button>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {caseWorkflow.template_name}
                  </Typography>
                  {caseWorkflow.completed_at && (
                    <Chip size="small" label="Completed" color="success" variant="outlined" />
                  )}
                </Box>
                {caseWorkflow.completed_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Finished {new Date(caseWorkflow.completed_at).toLocaleString()}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={caseWorkflow.completed_at ? 100 : caseWorkflow.progress_percent}
                    sx={{ flex: 1, height: 8, borderRadius: 1,
                      ...(caseWorkflow.completed_at ? { '& .MuiLinearProgress-bar': { bgcolor: 'success.main' } } : {}),
                    }}
                  />
                  <Typography variant="caption" fontWeight={600}>
                    {caseWorkflow.completed_at ? 100 : Math.round(caseWorkflow.progress_percent)}%
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {caseWorkflow.steps.filter((s) => s.status === 'completed').length} of {caseWorkflow.steps.length} steps
                </Typography>
                {caseWorkflow.completed_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Completion recorded in activity below.
                  </Typography>
                )}
                <List dense disablePadding sx={{ mb: 1 }}>
                  {caseWorkflow.steps
                    .slice()
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((step, index, sorted) => {
                      const isCompleted = step.status === 'completed';
                      const isNextOpen = !isCompleted && sorted.slice(0, index).every((s) => s.status === 'completed');
                      return (
                        <ListItem
                          key={step.id}
                          disablePadding
                          sx={{
                            alignItems: 'flex-start',
                            gap: 1,
                            py: 0.5,
                            position: 'relative',
                            pl: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              mr: 1.5,
                              mt: 0.25,
                            }}
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                ...(isCompleted
                                  ? { color: 'success.main', bgcolor: 'success.light' }
                                  : isNextOpen
                                    ? {
                                        border: '2px solid',
                                        borderColor: 'primary.main',
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        boxShadow: 1,
                                      }
                                    : {
                                        border: '2px solid',
                                        borderColor: 'action.disabled',
                                        bgcolor: 'action.hover',
                                      }),
                              }}
                            >
                              {isCompleted ? (
                                <CheckCircleIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <Typography variant="caption" fontWeight={700}>
                                  {index + 1}
                                </Typography>
                              )}
                            </Box>
                            {index < sorted.length - 1 && (
                              <Box
                                sx={{
                                  width: 2,
                                  minHeight: 16,
                                  bgcolor: isCompleted ? 'success.light' : 'action.disabledBackground',
                                  borderRadius: 1,
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ListItemText
                              primary={step.step_name}
                              secondary={step.step_description}
                              primaryTypographyProps={{
                                variant: 'body2',
                                fontWeight: isNextOpen ? 600 : undefined,
                                color: isNextOpen ? 'primary.main' : undefined,
                              }}
                              sx={{ flex: 1, minWidth: 0 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
                              <Select
                                value={step.status}
                                onChange={(e) => handleWorkflowStepStatusChange(step.id, e.target.value)}
                                displayEmpty
                                disabled={Boolean(caseWorkflow.completed_at)}
                              >
                                <MenuItem value="open">Open</MenuItem>
                                <MenuItem value="in_progress">In progress</MenuItem>
                                <MenuItem value="completed">Complete</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </ListItem>
                      );
                    })}
                </List>
                {!caseWorkflow.completed_at && caseWorkflow.steps.every((s) => s.status === 'completed') && (
                  <Button fullWidth size="small" variant="contained" color="primary" onClick={handleCompleteWorkflow}>
                    Mark workflow complete
                  </Button>
                )}
                {caseWorkflow.completed_at && (
                  <Button fullWidth size="small" variant="outlined" onClick={handleOpenApplyWorkflow} sx={{ mt: 1 }}>
                    Apply another workflow
                  </Button>
                )}
              </>
            )}
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Actions
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => {
                setEmailOpen(true);
                setEmailTo('');
                setEmailCc('');
                setEmailBcc('');
                setEmailSubject('');
                setEmailBody('');
                fetchContactsForEmail();
              }}
              sx={{ mb: 1 }}
            >
              Send email
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AttachIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload attachment'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={handleFileUpload}
            />
          </Paper>

          {caseData.attachments && caseData.attachments.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Attachments
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Filename</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {caseData.attachments.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.filename}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handlePreviewAttachment(a)}
                            title="Preview"
                          >
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadAttachment(a)}
                            title="Download"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(a.id)} title="Delete">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Description
            </Typography>
            {caseData.description ? (
              <Box
                sx={{
                  height: { xs: 'min(360px, 50vh)', sm: 400 },
                  maxHeight: '55vh',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  minWidth: 0,
                  pr: 0.5,
                  boxSizing: 'border-box',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  bgcolor: 'action.hover',
                }}
              >
                {looksLikeHtml(caseData.description) ? (
                  <Box
                    className="case-html-description"
                    sx={richHtmlSx}
                    dangerouslySetInnerHTML={{ __html: caseData.description }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    {caseData.description}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}
          </Paper>
        </Grid>

        {id && (
          <Grid item xs={12}>
            <RelatedTasksBlock caseId={id} />
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Activity timeline
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ minHeight: 120, mb: 1 }}>
                <ReactQuill
                  theme="snow"
                  value={noteText}
                  onChange={setNoteText}
                  placeholder="Add a note..."
                  style={{ minHeight: 100 }}
                  modules={NOTE_QUILL_MODULES}
                />
              </Box>
              <Button variant="contained" onClick={handleAddNote} disabled={addingNote || isQuillNoteEmpty(noteText)}>
                {addingNote ? <CircularProgress size={24} /> : 'Add note'}
              </Button>
            </Box>
            <List disablePadding>
              {(caseData.activity || []).map((act: CaseActivity, idx: number) => (
                <React.Fragment key={act.id}>
                  {idx > 0 && <Divider />}
                  <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                        {getActivityIcon(act.activity_type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primaryTypographyProps={{ variant: 'body2' }}
                      primary={
                        act.activity_type === 'note' && act.content ? (
                          <Box
                            component="div"
                            className="case-note-content"
                            sx={{
                              fontSize: '0.875rem',
                              '& p': { margin: '0 0 0.35em 0' },
                              '& p:last-child': { marginBottom: 0 },
                              '& ul, & ol': { margin: '0.35em 0', paddingLeft: 1.5 },
                              '& img': { maxWidth: '100%', height: 'auto', borderRadius: 4, display: 'block' },
                            }}
                            dangerouslySetInnerHTML={{ __html: act.content }}
                          />
                        ) : (act.activity_type === 'email_sent' || act.activity_type === 'customer_reply') && act.metadata ? (
                          <Box>
                            <Typography component="span" variant="body2">{act.content || act.activity_type}</Typography>
                            {(() => {
                              const m = act.metadata as Record<string, unknown>;
                              const body = m.body ? String(m.body) : (m.body_html ? stripHtml(String(m.body_html)) : '');
                              const previewLen = 150;
                              const hasMore = body.length > previewLen;
                              const preview = body ? (hasMore ? body.slice(0, previewLen).trim() + '...' : body) : null;
                              const openEmailModal = () => {
                                const isReply = act.activity_type === 'customer_reply';
                                setEmailViewData({
                                  to: String(isReply ? (m.from || '') : (m.to || '')),
                                  subject: String(m.subject || ''),
                                  cc: m.cc ? String(m.cc) : undefined,
                                  bcc: m.bcc ? String(m.bcc) : undefined,
                                  body: m.body ? String(m.body) : (m.full_email ? String(m.full_email) : undefined),
                                  body_html: m.body_html ? String(m.body_html) : undefined,
                                  isReply,
                                });
                                setEmailViewOpen(true);
                              };
                              return (
                                <>
                                  {preview && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8125rem' }}>
                                      {preview}
                                    </Typography>
                                  )}
                                  <Button
                                    size="small"
                                    startIcon={<ExpandMoreIcon />}
                                    onClick={openEmailModal}
                                    sx={{ mt: 0.25, p: 0, minWidth: 0, fontSize: '0.75rem' }}
                                  >
                                    View full email
                                  </Button>
                                </>
                              );
                            })()}
                          </Box>
                        ) : (
                          act.content || act.activity_type
                        )
                      }
                      secondary={
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {act.user_name || 'System'} • {formatDateTime(act.created_at)}
                          </Typography>
                        </>
                      }
                    />
                    {act.activity_type === 'note' && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleActivityMenuOpen(e, act)}
                        aria-label="Note actions"
                        aria-controls={activityMenuAnchor ? 'activity-note-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={activityMenuAnchor ? 'true' : undefined}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
            <Menu
              id="activity-note-menu"
              anchorEl={activityMenuAnchor}
              open={Boolean(activityMenuAnchor)}
              onClose={handleActivityMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleDeleteNote}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            </Menu>
            {(!caseData.activity || caseData.activity.length === 0) && (
              <Typography color="text.secondary">No activity yet</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {previewAttachment?.filename}
          <Box>
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => previewAttachment && handleDownloadAttachment({ id: previewAttachment.id, filename: previewAttachment.filename })}
              size="small"
              sx={{ mr: 1 }}
            >
              Download
            </Button>
            <IconButton size="small" onClick={handleClosePreview} aria-label="Close"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, bgcolor: 'grey.100' }}>
          {previewLoading && <CircularProgress />}
          {!previewLoading && previewAttachment && (() => {
            const ct = (previewAttachment.content_type || '').toLowerCase();
            const ext = previewAttachment.filename.split('.').pop()?.toLowerCase() || '';
            const isImage = ct.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
            const isPdf = ct === 'application/pdf' || ext === 'pdf';
            const isText = ct.startsWith('text/') || ct.includes('json') || ct.includes('xml') || ['txt', 'json', 'xml', 'csv', 'log', 'md', 'yaml', 'yml'].includes(ext);
            if (isImage && previewUrl) {
              return (
                <Box component="img" src={previewUrl} alt={previewAttachment.filename} sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              );
            }
            if (isPdf && previewUrl) {
              return (
                <iframe src={previewUrl} title={previewAttachment.filename} style={{ width: '100%', height: '70vh', border: 'none' }} />
              );
            }
            if (isText && previewTextContent !== null) {
              return (
                <Box sx={{ width: '100%', height: '70vh', overflow: 'auto', p: 2 }}>
                  <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {previewTextContent}
                  </Typography>
                </Box>
              );
            }
            if (!isImage && !isPdf && !isText) {
              return (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography color="text.secondary">Preview not available for this file type.</Typography>
                  <Button startIcon={<DownloadIcon />} onClick={() => handleDownloadAttachment({ id: previewAttachment.id, filename: previewAttachment.filename })} sx={{ mt: 2 }}>
                    Download file
                  </Button>
                </Box>
              );
            }
            return null;
          })()}
        </DialogContent>
      </Dialog>

      <CaseFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSuccess={() => { setEditOpen(false); fetchCase(); }}
        accountId={caseData.account_id}
        caseData={caseData}
        mode="edit"
        caseId={id}
      />

      <Dialog open={applyWorkflowOpen} onClose={() => setApplyWorkflowOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Apply workflow</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Workflow template</InputLabel>
            <Select value={applyWorkflowTemplateId} label="Workflow template" onChange={(e) => setApplyWorkflowTemplateId(e.target.value)}>
              <MenuItem value="">Select a workflow</MenuItem>
              {workflowTemplates.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyWorkflowOpen(false)}>Cancel</Button>
          <Button onClick={handleApplyWorkflow} variant="contained" disabled={!applyWorkflowTemplateId || applyWorkflowSubmitting}>
            {applyWorkflowSubmitting ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Send email</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minHeight: 360 }}>
            <FormControl fullWidth>
              <InputLabel>Template (optional)</InputLabel>
              <Select
                value={selectedTemplateId}
                label="Template"
                onChange={(e) => handleTemplateSelect(e.target.value)}
              >
                <MenuItem value="">Custom</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              freeSolo
              options={accountContacts.filter((c) => c.email).map((c) => ({ email: c.email!, label: `${c.full_name || 'Contact'} <${c.email}>` }))}
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt as { email: string; label: string }).label)}
              inputValue={emailTo}
              onInputChange={(_, v) => setEmailTo(v)}
              onChange={(_, v) => setEmailTo(typeof v === 'string' ? v : (v as { email: string } | null)?.email ?? '')}
              loading={contactsLoading}
              renderInput={(params) => (
                <TextField {...params} label="To" required placeholder="Select contact or type email" />
              )}
            />
            <TextField label="CC" value={emailCc} onChange={(e) => setEmailCc(e.target.value)} fullWidth placeholder="Optional" />
            <TextField label="BCC" value={emailBcc} onChange={(e) => setEmailBcc(e.target.value)} fullWidth placeholder="Optional" />
            <TextField
              label="Subject"
              required
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              fullWidth
              placeholder="e.g. Re: {{case_number}} – {{subject}}"
            />
            <Box sx={{ minHeight: 200 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Body</Typography>
              <ReactQuill theme="snow" value={emailBody} onChange={setEmailBody} style={{ minHeight: 180 }} />
            </Box>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Available placeholders (use in subject and body)
              </Typography>
              <Typography variant="caption" component="span" sx={{ wordBreak: 'break-all' }}>
                Subject &amp; body: {CASE_EMAIL_PLACEHOLDERS.join(', ')}. Body only: {BODY_PLACEHOLDER} (inserts your message).
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendEmail} disabled={sendingEmail || !emailTo.trim() || !emailSubject.trim() || !emailBody.trim()}>
            {sendingEmail ? <CircularProgress size={24} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reopenOpen} onClose={() => setReopenOpen(false)}>
        <DialogTitle>Reopen case</DialogTitle>
        <DialogContent>
          <Typography>Set this case back to In Progress?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleReopen} disabled={reopening}>
            {reopening ? <CircularProgress size={24} /> : 'Reopen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mergeOpen} onClose={() => { setMergeOpen(false); setMergeSearch(''); setMergeResults([]); setMergeSelectedId(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Merge case into {caseData.case_number}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select another case to merge into this one. Its activity and attachments will be moved here.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by case number or subject..."
              value={mergeSearch}
              onChange={(e) => setMergeSearch(e.target.value)}
              fullWidth
              onKeyDown={(e) => e.key === 'Enter' && handleMergeSearch()}
            />
            <Button variant="outlined" onClick={handleMergeSearch}>Search</Button>
          </Box>
          <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
            {mergeResults.length === 0 && mergeSearch && (
              <Typography color="text.secondary">No matching cases found</Typography>
            )}
            {mergeResults.map((c) => (
              <Box
                key={c.id}
                onClick={() => setMergeSelectedId(mergeSelectedId === c.id ? null : c.id)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderRadius: 1,
                  bgcolor: mergeSelectedId === c.id ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                  mb: 0.5,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  <Box component="span" sx={{ color: 'text.secondary', mr: 0.75 }}>
                    {c.case_number}
                  </Box>
                  <Box component="span">{c.subject}</Box>
                </Typography>
                <Typography variant="caption" color="text.secondary">{c.account_display_name || c.account_id}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMergeOpen(false); setMergeSearch(''); setMergeResults([]); setMergeSelectedId(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleMergeConfirm} disabled={!mergeSelectedId || merging}>
            {merging ? <CircularProgress size={24} /> : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailViewOpen} onClose={() => setEmailViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{emailViewData?.isReply ? 'Customer reply' : 'Sent email'}</DialogTitle>
        <DialogContent>
          {emailViewData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{emailViewData.isReply ? 'From' : 'To'}</Typography>
                <Typography variant="body2">{emailViewData.to}</Typography>
              </Box>
              {emailViewData.cc && (
                <Box>
                  <Typography variant="caption" color="text.secondary">CC</Typography>
                  <Typography variant="body2">{emailViewData.cc}</Typography>
                </Box>
              )}
              {emailViewData.bcc && (
                <Box>
                  <Typography variant="caption" color="text.secondary">BCC</Typography>
                  <Typography variant="body2">{emailViewData.bcc}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Subject</Typography>
                <Typography variant="body2" fontWeight={600}>{emailViewData.subject}</Typography>
              </Box>
              <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Body</Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    maxHeight: 300,
                    overflow: 'auto',
                    fontSize: '0.875rem',
                    '& p': { margin: '0 0 0.35em 0' },
                  }}
                >
                  {emailViewData.body_html ? (
                    <div dangerouslySetInnerHTML={{ __html: emailViewData.body_html }} />
                  ) : (
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>
                      {emailViewData.body || '(No body stored)'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseDetailPage;
